import { createTheme } from "@material-ui/core/styles";
import fluyraLogoLight from "../assets/fluyra-logo-light.png";
import fluyraLogoDark from "../assets/fluyra-logo-dark.png";
import fluyraFavicon from "../assets/fluyra-favicon.png";
import fluyraSidebarLogoDark from "../assets/fluyra-sidebar-logo-dark.png";

export const FLUYRA_THEME_STORAGE_KEY = "fluyra-theme";
export const FLUYRA_THEME_EXPLICIT_STORAGE_KEY = "fluyra-theme-explicit";
export const FLUYRA_ALLOWED_THEMES = ["dark", "light"];
export const FLUYRA_DEFAULT_THEME = "light";

export const FLUYRA_BRAND = {
  appName: "Fluyra CRM",
  accent: "#ff0052",
  accentHover: "#e6004a",
  accentSoftDark: "rgba(255, 0, 82, 0.12)",
  accentSoftLight: "rgba(255, 0, 82, 0.08)",
  sidebarBackground: "#0a1b22",
  sidebarSurface: "#112a36",
  sidebarHover: "#0c2028",
  sidebarBorder: "rgba(241, 241, 241, 0.08)",
};

export const FLUYRA_TOKENS = {
  dark: {
    bgPrimary: "#050d11",
    bgSecondary: "#0a1b22",
    surface: "#112a36",
    surfaceHover: "#0c2028",
    card: "#112a36",
    textPrimary: "#f1f1f1",
    textSecondary: "#a1b2b8",
    textMuted: "#6b7c85",
    border: "rgba(241, 241, 241, 0.08)",
    accentSoft: "rgba(255, 0, 82, 0.12)",
    inputBg: "#0a1b22",
    inputBorder: "rgba(241, 241, 241, 0.12)",
  },
  light: {
    bgPrimary: "#f7f9fb",
    bgSecondary: "#f1f1f1",
    surface: "#ffffff",
    surfaceHover: "#f1f5f9",
    card: "#ffffff",
    textPrimary: "#0a1b22",
    textSecondary: "#4b5563",
    textMuted: "#9ca3af",
    border: "#e5e7eb",
    accentSoft: "rgba(255, 0, 82, 0.08)",
    inputBg: "#ffffff",
    inputBorder: "#d1d5db",
  },
};

export const getStoredFluyraTheme = () => {
  const hasExplicitThemePreference =
    window.localStorage.getItem(FLUYRA_THEME_EXPLICIT_STORAGE_KEY) === "true";
  const hasAuthToken = Boolean(window.localStorage.getItem("token"));

  if (!hasExplicitThemePreference && !hasAuthToken) {
    return FLUYRA_DEFAULT_THEME;
  }

  const storedTheme = window.localStorage.getItem(FLUYRA_THEME_STORAGE_KEY);
  if (FLUYRA_ALLOWED_THEMES.includes(storedTheme)) {
    return storedTheme;
  }

  const legacyTheme = window.localStorage.getItem("preferredTheme");
  if (FLUYRA_ALLOWED_THEMES.includes(legacyTheme)) {
    return legacyTheme;
  }

  return FLUYRA_DEFAULT_THEME;
};

export const applyFluyraTheme = (theme) => {
  const safeTheme = FLUYRA_ALLOWED_THEMES.includes(theme)
    ? theme
    : FLUYRA_DEFAULT_THEME;
  document.documentElement.setAttribute("data-theme", safeTheme);
  window.localStorage.setItem(FLUYRA_THEME_STORAGE_KEY, safeTheme);
  window.localStorage.setItem("preferredTheme", safeTheme);
};

export const markFluyraThemeAsExplicit = () => {
  window.localStorage.setItem(FLUYRA_THEME_EXPLICIT_STORAGE_KEY, "true");
};

export const buildFluyraMuiTheme = (mode) => {
  const paletteMode = FLUYRA_TOKENS[mode] || FLUYRA_TOKENS[FLUYRA_DEFAULT_THEME];

  return createTheme({
    mode,
    padding: "16px",
    scrollbarStyles: {
      "&::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: FLUYRA_BRAND.accent,
        borderRadius: "999px",
        border: `2px solid ${paletteMode.bgSecondary}`,
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: paletteMode.bgSecondary,
      },
    },
    scrollbarStylesSoft: {
      "&::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: paletteMode.border,
        borderRadius: "999px",
      },
    },
    palette: {
      type: mode,
      primary: {
        main: FLUYRA_BRAND.accent,
        dark: FLUYRA_BRAND.accentHover,
        contrastText: "#ffffff",
      },
      secondary: {
        main: paletteMode.surface,
        contrastText: paletteMode.textPrimary,
      },
      background: {
        default: paletteMode.bgPrimary,
        paper: paletteMode.card,
        primary: paletteMode.bgPrimary,
      },
      text: {
        primary: paletteMode.textPrimary,
        secondary: paletteMode.textSecondary,
      },
      divider: paletteMode.border,
      action: {
        hover: paletteMode.surfaceHover,
        selected: paletteMode.accentSoft,
      },
      dark: { main: paletteMode.textPrimary },
      light: { main: paletteMode.textSecondary },
      textPrimary: paletteMode.textPrimary,
      borderPrimary: paletteMode.border,
      fontColor: paletteMode.textPrimary,
      tabHeaderBackground: paletteMode.surface,
      optionsBackground: paletteMode.surface,
      fancyBackground: paletteMode.bgSecondary,
      total: paletteMode.card,
      messageIcons: paletteMode.textSecondary,
      inputBackground: paletteMode.inputBg,
      barraSuperior: FLUYRA_BRAND.sidebarBackground,
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: '"Orkney", sans-serif',
      h1: {
        fontFamily: '"Monument Extended", sans-serif',
        letterSpacing: "-0.02em",
      },
      h2: {
        fontFamily: '"Monument Extended", sans-serif',
        letterSpacing: "-0.02em",
      },
      h3: {
        fontFamily: '"Monument Extended", sans-serif',
        letterSpacing: "-0.02em",
      },
      h4: {
        fontFamily: '"Monument Extended", sans-serif',
        letterSpacing: "-0.02em",
      },
      h5: {
        fontFamily: '"Monument Extended", sans-serif',
        letterSpacing: "-0.02em",
      },
      h6: {
        fontFamily: '"Monument Extended", sans-serif',
        letterSpacing: "-0.02em",
      },
      button: {
        textTransform: "none",
        fontWeight: 600,
      },
    },
    overrides: {
      MuiCssBaseline: {
        "@global": {
          body: {
            backgroundColor: paletteMode.bgPrimary,
            color: paletteMode.textPrimary,
          },
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: paletteMode.card,
          color: paletteMode.textPrimary,
          backgroundImage: "none",
        },
        outlined: {
          borderColor: paletteMode.border,
        },
      },
      MuiCard: {
        root: {
          backgroundColor: paletteMode.card,
          color: paletteMode.textPrimary,
          border: `1px solid ${paletteMode.border}`,
          boxShadow:
            mode === "dark"
              ? "0 18px 40px rgba(0, 0, 0, 0.22)"
              : "0 18px 40px rgba(15, 23, 42, 0.08)",
        },
      },
      MuiButton: {
        containedPrimary: {
          backgroundColor: FLUYRA_BRAND.accent,
          color: "#ffffff",
          "&:hover": {
            backgroundColor: FLUYRA_BRAND.accentHover,
          },
        },
        outlinedPrimary: {
          color: FLUYRA_BRAND.accent,
          border: `1px solid ${mode === "dark" ? paletteMode.border : FLUYRA_BRAND.accentSoftLight}`,
          "&:hover": {
            backgroundColor: paletteMode.accentSoft,
            borderColor: FLUYRA_BRAND.accent,
          },
        },
      },
      MuiInputBase: {
        root: {
          color: paletteMode.textPrimary,
        },
        input: {
          "&::placeholder": {
            color: paletteMode.textMuted,
            opacity: 1,
          },
        },
      },
      MuiBadge: {
        colorSecondary: {
          backgroundColor: FLUYRA_BRAND.accent,
          color: "#ffffff",
        },
      },
      MuiTab: {
        root: {
          textTransform: "none",
        },
      },
    },
    appLogoLight: fluyraLogoLight,
    appLogoDark: fluyraLogoDark,
    sidebarLogoLight: fluyraSidebarLogoDark,
    sidebarLogoDark: fluyraSidebarLogoDark,
    appLogoFavicon: fluyraFavicon,
    appName: FLUYRA_BRAND.appName,
    calculatedLogoLight: () => fluyraLogoLight,
    calculatedLogoDark: () => fluyraLogoDark,
    calculatedSidebarLogoLight: () => fluyraSidebarLogoDark,
    calculatedSidebarLogoDark: () => fluyraSidebarLogoDark,
    fluyra: {
      brand: FLUYRA_BRAND,
      tokens: paletteMode,
    },
  });
};
